import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiRefreshCw, FiCalendar, FiX } from 'react-icons/fi';
import { articleService } from '../services/articleService';
import { toast } from 'react-hot-toast';
import { countryOptions, getCountryCode, getCountryName } from '../utils/countries';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ArticleDetailScreen = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [formData, setFormData] = useState({
        nombre: '',
        ciudad: '',
        pais: '',
        activo: true
    });

    // Estado para filtro de fechas
    const [dateFilter, setDateFilter] = useState({
        startDate: '',
        endDate: '',
        active: false
    });

    // Estado para ordenamiento de productos
    const [productSorting, setProductSorting] = useState({
        field: 'totalSales',
        order: 'DESC'
    });

    // Estado para paginación de productos
    const [productPagination, setProductPagination] = useState({
        page: 1,
        limit: 10
    });

    // Opciones de límite de paginación para productos
    const productLimitOptions = [5, 10, 20, 50];

    // Estados para el gráfico temporal
    const [groupBy, setGroupBy] = useState('day');
    const [chartSalesFilter, setChartSalesFilter] = useState('all'); // 'all' o 'approved_pending'
    const [isStacked, setIsStacked] = useState(false);
    const [selectedChartProducts, setSelectedChartProducts] = useState([]);
    const [salesData, setSalesData] = useState([]);
    const originalSalesRef = useRef(null);

    // Función para manejar ordenamiento de productos
    const handleProductSort = (field) => {
        setProductSorting(prev => ({
            field,
            order: prev.field === field && prev.order === 'DESC' ? 'ASC' : 'DESC'
        }));
        // Resetear a la primera página cuando se cambia el ordenamiento
        setProductPagination(prev => ({ ...prev, page: 1 }));
    };

    // Funciones para paginación de productos
    const handleProductPageChange = (newPage) => {
        setProductPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleProductLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setProductPagination({
            page: 1,
            limit: newLimit
        });
    };

    // Función para renderizar iconos de ordenamiento
    const renderProductSortIcon = (field) => {
        if (productSorting.field !== field) return null;
        return productSorting.order === 'ASC' ? ' ↑' : ' ↓';
    };

    // Función para manejar cambios en el filtro de fechas
    const handleDateFilterChange = (field, value) => {
        setDateFilter(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Función para aplicar filtro de fechas
    const applyDateFilter = () => {
        if (!dateFilter.startDate && !dateFilter.endDate) {
            toast.error('Selecciona al menos una fecha');
            return;
        }
        
        if (dateFilter.startDate && dateFilter.endDate && dateFilter.startDate > dateFilter.endDate) {
            toast.error('La fecha de inicio debe ser anterior a la fecha de fin');
            return;
        }

        const nextFilter = { ...dateFilter, active: true };
        setDateFilter(nextFilter);
        loadArticle(nextFilter);
    };

    // Función para limpiar filtro de fechas
    const clearDateFilter = () => {
        const nextFilter = {
            startDate: '',
            endDate: '',
            active: false
        };
        setDateFilter(nextFilter);
        loadArticle(nextFilter);
    };

    // Función para obtener filtros rápidos de fecha
    const getQuickDateFilters = () => {
        const today = new Date();
        const filters = [
            {
                label: 'Último mes',
                startDate: new Date(today.getFullYear(), today.getMonth() - 1, today.getDate()).toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            },
            {
                label: 'Últimos 3 meses',
                startDate: new Date(today.getFullYear(), today.getMonth() - 3, today.getDate()).toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            },
            {
                label: 'Último año',
                startDate: new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()).toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            },
            {
                label: 'Este año',
                startDate: new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            }
        ];
        return filters;
    };

    // Función para aplicar filtro rápido
    const applyQuickDateFilter = (filter) => {
        const nextFilter = {
            startDate: filter.startDate,
            endDate: filter.endDate,
            active: true
        };
        setDateFilter(nextFilter);
        loadArticle(nextFilter);
    };

    // Función para formatear fechas según el agrupamiento
    const formatDate = (date, groupBy) => {
        if (!date) return '';
        
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        
        try {
            switch (groupBy) {
                case 'day':
                    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                case 'week':
                    const weekStart = new Date(d);
                    weekStart.setDate(d.getDate() - d.getDay());
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekStart.getDate() + 6);
                    return `${weekStart.getDate()}/${weekStart.getMonth() + 1} - ${weekEnd.getDate()}/${weekEnd.getMonth() + 1}`;
                case 'month':
                    return d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
                case 'year':
                    return d.getFullYear().toString();
                default:
                    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
            }
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    // Función para obtener la clave de agrupación según el período
    const getGroupKey = (date, groupBy) => {
        const d = new Date(date);
        switch (groupBy) {
            case 'day':
                return d.toISOString().split('T')[0];
            case 'week':
                const weekStart = new Date(d);
                weekStart.setDate(d.getDate() - d.getDay());
                return weekStart.toISOString().split('T')[0];
            case 'month':
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            case 'year':
                return d.getFullYear().toString();
            default:
                return d.toISOString().split('T')[0];
        }
    };

    // Función para obtener todas las fechas en un rango
    const getAllDatesInRange = (startDate, endDate, groupBy) => {
        const dates = [];
        const currentDate = new Date(startDate);
        const lastDate = new Date(endDate);

        while (currentDate <= lastDate) {
            dates.push(getGroupKey(currentDate.toISOString(), groupBy));
            switch (groupBy) {
                case 'day':
                    currentDate.setDate(currentDate.getDate() + 1);
                    break;
                case 'week':
                    currentDate.setDate(currentDate.getDate() + 7);
                    break;
                case 'month':
                    currentDate.setMonth(currentDate.getMonth() + 1);
                    break;
                case 'year':
                    currentDate.setFullYear(currentDate.getFullYear() + 1);
                    break;
            }
        }
        return dates;
    };

    // Función para generar colores únicos para productos
    const generateProductColor = (index) => {
        const colors = [
            'rgb(255, 99, 132)',   // Rojo
            'rgb(54, 162, 235)',   // Azul
            'rgb(255, 205, 86)',   // Amarillo
            'rgb(75, 192, 192)',   // Verde agua
            'rgb(153, 102, 255)',  // Púrpura
            'rgb(255, 159, 64)',   // Naranja
            'rgb(199, 199, 199)',  // Gris
            'rgb(83, 102, 255)',   // Azul índigo
            'rgb(255, 99, 255)',   // Magenta
            'rgb(99, 255, 132)',   // Verde lima
        ];
        
        if (index >= colors.length) {
            const hue = (index * 137.508) % 360;
            return `hsl(${hue}, 70%, 50%)`;
        }
        
        return colors[index];
    };

    // Función para obtener la versión rgba de un color
    const getBackgroundColor = (color, opacity = 0.5) => {
        if (color.startsWith('hsl')) {
            return color.replace('hsl', 'hsla').replace(')', `, ${opacity})`);
        }
        return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
    };

    // Función para agrupar ventas por período
    const groupSalesByPeriod = (sales, groupBy, selectedProductIds, salesFilter = 'all') => {
        if (!Array.isArray(sales) || sales.length === 0) return [];

        // Filtrar las ventas según el tipo seleccionado
        let filteredSales = sales.filter(sale => sale && sale.saleDate);
        if (salesFilter === 'approved_pending') {
            filteredSales = filteredSales.filter(sale => sale.status === 'approved' || sale.status === 'pending');
        }

        if (filteredSales.length === 0) return [];

        const dates = filteredSales.map(sale => new Date(sale.saleDate)).filter(date => !isNaN(date));
        if (dates.length === 0) return [];
        
        const startDate = new Date(Math.min(...dates));
        const endDate = new Date(Math.max(...dates));
        const allDates = getAllDatesInRange(startDate, endDate, groupBy);
        
        const grouped = {};
        allDates.forEach(date => {
            grouped[date] = {
                date: date,
                sales: 0,
                revenue: 0,
                products: {}
            };
            
            // Inicializar datos para cada producto seleccionado
            if (Array.isArray(selectedProductIds)) {
                selectedProductIds.forEach(productId => {
                    if (productId) {
                        grouped[date].products[productId] = {
                            sales: 0,
                            revenue: 0
                        };
                    }
                });
            }
        });
        
        filteredSales.forEach(sale => {
            if (!sale || !sale.saleDate) return;
            
            const groupKey = getGroupKey(sale.saleDate, groupBy);
            if (grouped[groupKey]) {
                grouped[groupKey].sales += 1;
                
                const commissionValue = parseFloat(sale.commissionEUR) || 0;
                grouped[groupKey].revenue += commissionValue;
                
                // Agrupar por producto si está seleccionado
                const productId = sale.Product?.id;
                if (productId && grouped[groupKey].products && grouped[groupKey].products[productId]) {
                    grouped[groupKey].products[productId].sales += 1;
                    grouped[groupKey].products[productId].revenue += commissionValue;
                }
            }
        });

        return Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    // Función para manejar selección de productos en el gráfico
    const handleChartProductSelection = (productId, isChecked) => {
        if (isChecked) {
            setSelectedChartProducts(prev => [...prev, productId]);
        } else {
            setSelectedChartProducts(prev => prev.filter(id => id !== productId));
        }
    };

    // Función para ordenar productos
    const sortedProducts = React.useMemo(() => {
        if (!article?.topProducts) return [];
        
        const products = [...article.topProducts];
        
        return products.sort((a, b) => {
            let aValue, bValue;
            
            switch (productSorting.field) {
                case 'totalSales':
                    aValue = a.totalSales || 0;
                    bValue = b.totalSales || 0;
                    break;
                case 'totalCommissionEUR':
                    aValue = parseFloat(a.totalCommissionEUR || 0);
                    bValue = parseFloat(b.totalCommissionEUR || 0);
                    break;
                case 'avgCommissionEUR':
                    aValue = parseFloat(a.avgCommissionEUR || 0);
                    bValue = parseFloat(b.avgCommissionEUR || 0);
                    break;
                case 'canceledPercentage':
                    aValue = parseFloat(a.canceledPercentage || 0);
                    bValue = parseFloat(b.canceledPercentage || 0);
                    break;
                case 'productName':
                    aValue = a.productName || '';
                    bValue = b.productName || '';
                    return productSorting.order === 'ASC' 
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                default:
                    return 0;
            }
            
            if (productSorting.order === 'ASC') {
                return aValue - bValue;
            } else {
                return bValue - aValue;
            }
        });
    }, [article?.topProducts, productSorting]);

    // Productos paginados
    const paginatedProducts = React.useMemo(() => {
        const startIndex = (productPagination.page - 1) * productPagination.limit;
        const endIndex = startIndex + productPagination.limit;
        return sortedProducts.slice(startIndex, endIndex);
    }, [sortedProducts, productPagination]);

    // Calcular estadísticas de paginación
    const productPaginationStats = React.useMemo(() => {
        const total = sortedProducts.length;
        const pages = Math.ceil(total / productPagination.limit);
        return {
            total,
            pages,
            hasMore: productPagination.page < pages
        };
    }, [sortedProducts.length, productPagination]);

    const loadArticle = async (filterState = dateFilter) => {
        try {
            setLoading(true);
            
            // Construir filtros para el backend
            const filters = {};
            if (filterState.active && filterState.startDate) {
                filters.startDate = filterState.startDate;
            }
            if (filterState.active && filterState.endDate) {
                filters.endDate = filterState.endDate;
            }
            
            const data = await articleService.getArticleById(id, filters);
            setArticle(data);
            setFormData({
                nombre: data.nombre || '',
                ciudad: data.ciudad || '',
                pais: getCountryCode(data.pais) || '',
                activo: data.activo !== undefined ? data.activo : true
            });
        } catch (error) {
            console.error('Error al cargar artículo:', error);
            toast.error('Error al cargar el artículo: ' + (error.response?.data?.message || error.message));
            if (error.response?.status === 404) {
                navigate('/articles');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadArticle();
    }, [id]);

    // Almacenar las ventas originales
    useEffect(() => {
        if (article?.recentSales) {
            originalSalesRef.current = article.recentSales;
            setSalesData(article.recentSales);
        }
    }, [article?.recentSales]);

    // Inicializar productos seleccionados con los 3 primeros para el gráfico
    useEffect(() => {
        if (Array.isArray(article?.topProducts) && article.topProducts.length > 0) {
            const top3Products = article.topProducts
                .slice(0, 3)
                .filter(product => product?.productId)
                .map(product => product.productId);
            setSelectedChartProducts(top3Products);
        }
    }, [article?.topProducts]);

    // Procesar datos del gráfico cuando cambien los productos seleccionados
    useEffect(() => {
        if (originalSalesRef.current && Array.isArray(selectedChartProducts)) {
            const processedData = groupSalesByPeriod(
                originalSalesRef.current,
                groupBy,
                selectedChartProducts,
                chartSalesFilter
            );
            setSalesData(processedData);
        } else {
            setSalesData([]);
        }
    }, [selectedChartProducts, groupBy, chartSalesFilter]);

    const handleEditClick = () => {
        setShowEditModal(true);
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setFormData({
            nombre: article?.nombre || '',
            ciudad: article?.ciudad || '',
            pais: getCountryCode(article?.pais) || '',
            activo: article?.activo !== undefined ? article.activo : true
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await articleService.updateArticle(id, formData);
            toast.success('Artículo actualizado correctamente');
            setShowEditModal(false);
            await loadArticle();
        } catch (error) {
            console.error('Error al actualizar artículo:', error);
            toast.error('Error al actualizar el artículo: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleRecalculateStats = async () => {
        try {
            toast.loading('Recalculando estadísticas...', { id: 'recalculating' });
            await articleService.recalculateArticleStats(id);
            toast.success('Estadísticas recalculadas correctamente', { id: 'recalculating' });
            await loadArticle();
        } catch (error) {
            console.error('Error al recalcular estadísticas:', error);
            toast.error('Error al recalcular estadísticas: ' + (error.response?.data?.message || error.message), { id: 'recalculating' });
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center p-8">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="text-center p-8">
                <h2 className="text-2xl font-bold mb-4">Artículo no encontrado</h2>
                <p className="text-base-content/70 mb-4">El artículo que buscas no existe o ha sido eliminado.</p>
                <Link to="/articles" className="btn btn-primary">
                    Volver a Artículos
                </Link>
            </div>
        );
    }

    // Procesar estadísticas de sufijos
    const estadisticasSufijos = article.estadisticasSufijos ? 
        (typeof article.estadisticasSufijos === 'string' ? 
            JSON.parse(article.estadisticasSufijos) : 
            article.estadisticasSufijos) : {};

    // Formatter para moneda
    const formatter = new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR'
    });

    // Configuración del gráfico de ventas
    const salesChartData = {
        labels: (Array.isArray(salesData) ? salesData : []).map(item => formatDate(item?.date, groupBy)),
        datasets: [
            {
                label: 'Total de Ventas',
                data: (Array.isArray(salesData) ? salesData : []).map(item => item?.sales || 0),
                borderColor: 'rgb(54, 162, 235)',
                backgroundColor: 'rgba(54, 162, 235, 0.3)',
                tension: 0.1,
                borderWidth: 3,
                fill: false,
                stack: isStacked ? 'total' : undefined
            },
            ...((Array.isArray(article?.topProducts) ? article.topProducts : [])
                .filter(product => product && selectedChartProducts.includes(product.productId))
                .map((product, index) => {
                    const color = generateProductColor(index);
                    return {
                        label: product?.productName && product.productName.length > 30 ? 
                            product.productName.substring(0, 30) + '...' : 
                            product?.productName || `Producto ${index + 1}`,
                        data: (Array.isArray(salesData) ? salesData : []).map(item => 
                            item?.products?.[product?.productId]?.sales || 0
                        ),
                        borderColor: color,
                        backgroundColor: isStacked ? getBackgroundColor(color, 0.8) : getBackgroundColor(color, 0.2),
                        tension: 0.1,
                        borderWidth: 2,
                        borderDash: isStacked ? [] : [5, 5],
                        fill: isStacked ? 'origin' : false,
                        stack: isStacked ? 'products' : undefined
                    };
                })
            )
        ]
    };

    const salesChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    maxWidth: 200,
                    usePointStyle: true
                }
            },
            title: {
                display: true,
                text: `Ventas por ${groupBy === 'day' ? 'Día' : 
                    groupBy === 'week' ? 'Semana' : 
                    groupBy === 'month' ? 'Mes' : 'Año'}`
            }
        },
        scales: {
            x: {
                stacked: isStacked
            },
            y: {
                beginAtZero: true,
                stacked: isStacked,
                title: {
                    display: true,
                    text: 'Número de Ventas'
                }
            }
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="page-header">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate('/articles')}
                        className="btn btn-ghost btn-circle"
                    >
                        <FiArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="page-title">{article.nombre}</h1>
                        <p className="text-base-content/70 font-mono">{article.identificador}</p>
                    </div>
                </div>
                <div className="page-actions">
                    <button 
                        onClick={handleRecalculateStats}
                        className="btn btn-outline"
                        title="Recalcular estadísticas"
                    >
                        <FiRefreshCw size={18} />
                        Recalcular
                    </button>
                    <button 
                        onClick={handleEditClick}
                        className="btn btn-primary"
                    >
                        <FiEdit size={18} />
                        Editar
                    </button>
                </div>
            </div>

            {/* Sección combinada: Enlaces rápidos y Filtro de fechas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Enlaces rápidos - Izquierda */}
                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <h2 className="card-title mb-4">Enlaces Rápidos</h2>
                        <div className="flex flex-col gap-3">
                            <Link 
                                to={`/sales?article=${article.identificador}${dateFilter.active ? 
                                    `${dateFilter.startDate ? `&date_from=${dateFilter.startDate}` : ''}${dateFilter.endDate ? `&date_to=${dateFilter.endDate}` : ''}` : 
                                    ''}`}
                                className="btn btn-outline"
                            >
                                {dateFilter.active ? 'Ver ventas filtradas' : 'Ver todas las ventas'}
                            </Link>
                            <Link 
                                to={`/sales?article=${article.identificador}&date_from=${new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]}`}
                                className="btn btn-outline"
                            >
                                Ventas último mes
                            </Link>
                            <Link 
                                to={`/sales?article=${article.identificador}&date_from=${new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0]}`}
                                className="btn btn-outline"
                            >
                                Ventas último año
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Filtro de fechas - Derecha */}
                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <FiCalendar className="text-xl text-primary" />
                                <h2 className="card-title">Filtro de Fechas</h2>
                                {dateFilter.active && (
                                    <div className="badge badge-primary badge-sm">Activo</div>
                                )}
                            </div>
                            
                            {/* Botón limpiar filtro */}
                            {dateFilter.active && (
                                <button
                                    onClick={clearDateFilter}
                                    className="btn btn-ghost btn-sm"
                                    title="Limpiar filtro"
                                >
                                    <FiX className="text-lg" />
                                    Limpiar
                                </button>
                            )}
                        </div>
                        
                        {/* Información del filtro aplicado */}
                        {dateFilter.active && article?.filtroFechas?.aplicado && (
                            <div className="mb-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
                                <div className="flex items-center gap-2 text-primary">
                                    <FiCalendar className="text-sm" />
                                    <span className="text-sm font-medium">
                                        Período filtrado:
                                        {article.filtroFechas.startDate && ` desde ${new Date(article.filtroFechas.startDate).toLocaleDateString('es-ES')}`}
                                        {article.filtroFechas.endDate && ` hasta ${new Date(article.filtroFechas.endDate).toLocaleDateString('es-ES')}`}
                                    </span>
                                </div>
                            </div>
                        )}
                        
                        <div className="space-y-4">
                            {/* Filtros rápidos */}
                            <div>
                                <label className="label">
                                    <span className="label-text font-medium">Filtros rápidos</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {getQuickDateFilters().map((filter, index) => (
                                        <button
                                            key={index}
                                            onClick={() => applyQuickDateFilter(filter)}
                                            className="btn btn-outline btn-sm"
                                        >
                                            {filter.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Divisor */}
                            <div className="divider my-2">
                                <span className="text-xs text-base-content/50">o selecciona fechas personalizadas</span>
                            </div>
                            
                            {/* Campos de fecha personalizados */}
                            <div>
                                <label className="label">
                                    <span className="label-text font-medium">Período personalizado</span>
                                </label>
                                <div className="flex flex-col gap-3">
                                    <div className="form-control">
                                        <label className="label py-1">
                                            <span className="label-text text-xs">Fecha de inicio</span>
                                        </label>
                                        <input
                                            type="date"
                                            className="input input-bordered input-sm w-full"
                                            value={dateFilter.startDate}
                                            onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
                                            placeholder="Seleccionar fecha de inicio"
                                        />
                                    </div>
                                    
                                    <div className="form-control">
                                        <label className="label py-1">
                                            <span className="label-text text-xs">Fecha de fin</span>
                                        </label>
                                        <input
                                            type="date"
                                            className="input input-bordered input-sm w-full"
                                            value={dateFilter.endDate}
                                            onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
                                            placeholder="Seleccionar fecha de fin"
                                        />
                                    </div>
                                    
                                    <button
                                        onClick={applyDateFilter}
                                        className="btn btn-primary btn-sm w-full"
                                        disabled={!dateFilter.startDate && !dateFilter.endDate}
                                    >
                                        <FiCalendar className="text-sm" />
                                        Aplicar Filtro
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Información General */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <h2 className="card-title text-primary">Ventas Válidas</h2>
                        <p className="text-3xl font-bold">{article.approvedPendingStats?.ventas || 0}</p>
                        <p className="text-sm text-base-content/70">
                            de {article.totalVentas} totales ({article.totalVentas > 0 ? ((article.approvedPendingStats?.ventas || 0) / article.totalVentas * 100).toFixed(1) : 0}%)
                        </p>
                    </div>
                </div>
                
                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <h2 className="card-title text-error">Ventas Canceladas</h2>
                        <p className="text-3xl font-bold text-error">{article.rejectedStats?.ventas || 0}</p>
                        <p className="text-sm text-base-content/70">
                            {article.totalVentas > 0 ? ((article.rejectedStats?.ventas || 0) / article.totalVentas * 100).toFixed(1) : 0}% del total
                        </p>
                    </div>
                </div>

                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <h2 className="card-title text-success">Comisión Válida</h2>
                        <p className="text-3xl font-bold text-success">
                            {parseFloat(article.approvedPendingStats?.comision || 0).toFixed(2)} €
                        </p>
                        <p className="text-sm text-base-content/70">
                            de {parseFloat(article.totalComision || 0).toFixed(2)} € totales
                        </p>
                    </div>
                </div>

                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <h2 className="card-title text-warning">Comisión Perdida</h2>
                        <p className="text-3xl font-bold text-warning">
                            {parseFloat(article.rejectedStats?.comision || 0).toFixed(2)} €
                        </p>
                        <p className="text-sm text-base-content/70">
                            {article.totalComision > 0 ? ((article.rejectedStats?.comision || 0) / article.totalComision * 100).toFixed(1) : 0}% del total
                        </p>
                    </div>
                </div>
            </div>

            {/* Información Adicional */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <h2 className="card-title text-info">Ubicación</h2>
                        <div className="space-y-1">
                            <p className="font-medium">
                                {getCountryName(article.pais)}
                            </p>
                            <p className="text-sm text-base-content/70">
                                {article.ciudad || 'Ciudad no especificada'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <h2 className="card-title">Estado</h2>
                        <div className="space-y-2">
                            <div className={`badge ${article.activo ? 'badge-success' : 'badge-error'} badge-lg`}>
                                {article.activo ? 'Activo' : 'Inactivo'}
                            </div>
                            <p className="text-sm text-base-content/70">
                                Última venta: {article.ultimaVenta ? 
                                    new Date(article.ultimaVenta).toLocaleDateString('es-ES') : 
                                    'Nunca'
                                }
                            </p>
                        </div>
                    </div>
                </div>

                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <h2 className="card-title text-accent">Eficiencia</h2>
                        <div className="space-y-2">
                            <p className="text-2xl font-bold">
                                {article.totalVentas > 0 ? 
                                    ((article.approvedPendingStats?.ventas || 0) / article.totalVentas * 100).toFixed(1) : 0
                                }%
                            </p>
                            <p className="text-sm text-base-content/70">
                                Ventas exitosas
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Estadísticas de Sufijos */}
            <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                    <h2 className="card-title mb-4">Estadísticas por Sufijo</h2>
                    {Object.keys(estadisticasSufijos).length === 0 ? (
                        <p className="text-base-content/70">No hay estadísticas de sufijos disponibles</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-zebra w-full">
                                <thead>
                                    <tr>
                                        <th>Sufijo</th>
                                        <th className="text-right">Ventas</th>
                                        <th className="text-right">Comisión</th>
                                        <th className="text-right">% del Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(estadisticasSufijos)
                                        .sort(([,a], [,b]) => (b.ventas || 0) - (a.ventas || 0))
                                        .map(([sufijo, stats]) => (
                                        <tr key={sufijo}>
                                            <td>
                                                <span className="font-mono">
                                                    {sufijo === 'sin_sufijo' ? '(sin sufijo)' : sufijo}
                                                </span>
                                            </td>
                                            <td className="text-right font-bold">
                                                {stats.ventas || 0}
                                            </td>
                                            <td className="text-right font-bold text-success">
                                                {parseFloat(stats.comision || 0).toFixed(2)} €
                                            </td>
                                            <td className="text-right">
                                                {article.totalVentas > 0 ? 
                                                    ((stats.ventas || 0) / article.totalVentas * 100).toFixed(1) : 0
                                                }%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Gráfico Temporal de Ventas */}
            {Array.isArray(article?.recentSales) && article.recentSales.length > 0 && (
                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="card-title">Evolución Temporal de Ventas</h2>
                            
                            {/* Controles del gráfico */}
                            <div className="flex gap-2">
                                <select 
                                    className="select select-bordered select-sm"
                                    value={groupBy}
                                    onChange={(e) => setGroupBy(e.target.value)}
                                >
                                    <option value="day">Por Día</option>
                                    <option value="week">Por Semana</option>
                                    <option value="month">Por Mes</option>
                                    <option value="year">Por Año</option>
                                </select>
                                
                                <select 
                                    className="select select-bordered select-sm"
                                    value={chartSalesFilter}
                                    onChange={(e) => setChartSalesFilter(e.target.value)}
                                >
                                    <option value="all">Todas las ventas</option>
                                    <option value="approved_pending">Aprobadas y pendientes</option>
                                </select>
                                
                                <select 
                                    className="select select-bordered select-sm"
                                    value={isStacked ? 'stacked' : 'normal'}
                                    onChange={(e) => setIsStacked(e.target.value === 'stacked')}
                                >
                                    <option value="normal">Superpuestos</option>
                                    <option value="stacked">Apilados</option>
                                </select>
                            </div>
                        </div>
                        
                        {/* Gráfico */}
                        <div className="h-96">
                            <Line data={salesChartData} options={salesChartOptions} />
                        </div>

                        {/* Selector de productos para el gráfico */}
                        <div className="mt-4">
                            <h3 className="text-lg font-medium mb-2">Productos en el gráfico:</h3>
                            <div className="flex flex-wrap gap-3">
                                {(Array.isArray(article?.topProducts) ? article.topProducts.slice(0, 10) : []).map((product, index) => (
                                    <label key={product?.productId || index} className="label cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            className="checkbox checkbox-primary checkbox-sm mr-2" 
                                            checked={product?.productId ? selectedChartProducts.includes(product.productId) : false}
                                            onChange={(e) => product?.productId && handleChartProductSelection(product.productId, e.target.checked)}
                                        />
                                        <span className="label-text text-sm">
                                            {product?.productName && product.productName.length > 25 ? 
                                                product.productName.substring(0, 25) + '...' : 
                                                product?.productName || `Producto ${index + 1}`}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Productos Más Vendidos */}
            <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                    <h2 className="card-title mb-4">Productos Más Vendidos</h2>
                    {!article.topProducts || article.topProducts.length === 0 ? (
                        <p className="text-base-content/70">No hay productos disponibles</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-zebra w-full">
                                <thead>
                                    <tr>
                                        <th>
                                            <label className="label cursor-pointer">
                                                <span className="label-text font-medium">Gráfico</span>
                                            </label>
                                        </th>
                                        <th 
                                            className="cursor-pointer" 
                                            onClick={() => handleProductSort('productName')}
                                        >
                                            Producto{renderProductSortIcon('productName')}
                                        </th>
                                        <th>Plataforma</th>
                                        <th 
                                            className="text-right cursor-pointer" 
                                            onClick={() => handleProductSort('totalSales')}
                                        >
                                            Ventas{renderProductSortIcon('totalSales')}
                                        </th>
                                        <th 
                                            className="text-right cursor-pointer" 
                                            onClick={() => handleProductSort('totalCommissionEUR')}
                                        >
                                            Comisión Total{renderProductSortIcon('totalCommissionEUR')}
                                        </th>
                                        <th 
                                            className="text-right cursor-pointer" 
                                            onClick={() => handleProductSort('avgCommissionEUR')}
                                        >
                                            Comisión Media{renderProductSortIcon('avgCommissionEUR')}
                                        </th>
                                        <th className="text-right">Estado</th>
                                        <th 
                                            className="text-right cursor-pointer" 
                                            onClick={() => handleProductSort('canceledPercentage')}
                                        >
                                            % Canceladas{renderProductSortIcon('canceledPercentage')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedProducts.map((product, index) => (
                                        <tr key={product.productId}>
                                            <td>
                                                <label className="label cursor-pointer justify-center">
                                                    <input 
                                                        type="checkbox" 
                                                        className="checkbox checkbox-primary" 
                                                        checked={selectedChartProducts.includes(product.productId)}
                                                        onChange={(e) => handleChartProductSelection(product.productId, e.target.checked)}
                                                    />
                                                </label>
                                            </td>
                                            <td>
                                                <div className="font-medium">
                                                    {product.productName}
                                                </div>
                                            </td>
                                            <td>
                                                <span 
                                                    className="badge badge-outline"
                                                    style={{ 
                                                        borderColor: product.platformColor || '#808080',
                                                        color: product.platformColor || '#808080'
                                                    }}
                                                >
                                                    {product.platformName}
                                                </span>
                                            </td>
                                            <td className="text-right font-bold">
                                                {product.totalSales}
                                            </td>
                                            <td className="text-right font-bold text-success">
                                                {parseFloat(product.totalCommissionEUR || 0).toFixed(2)} €
                                            </td>
                                            <td className="text-right">
                                                {parseFloat(product.avgCommissionEUR || 0).toFixed(2)} €
                                            </td>
                                            <td className="text-right">
                                                <div className="flex flex-col gap-1 text-xs">
                                                    <span className="text-success">✅ {product.approvedSales}</span>
                                                    <span className="text-warning">⏳ {product.pendingSales}</span>
                                                    <span className="text-error">❌ {product.rejectedSales}</span>
                                                </div>
                                            </td>
                                            <td className="text-right">
                                                <span className={`font-medium ${parseFloat(product.canceledPercentage) > 10 ? 'text-error' : 'text-base-content'}`}>
                                                    {product.canceledPercentage}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    {/* Controles de paginación para productos */}
                    {article.topProducts && article.topProducts.length > 0 && (
                        <div className="flex justify-between items-center mt-4">
                            <div className="flex items-center gap-4">
                                <div className="text-sm">
                                    Mostrando <span className="font-medium">{paginatedProducts.length}</span> de <span className="font-medium">{productPaginationStats.total}</span> productos
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">Mostrar:</span>
                                    <select 
                                        className="select select-bordered select-sm"
                                        value={productPagination.limit}
                                        onChange={handleProductLimitChange}
                                    >
                                        {productLimitOptions.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            {productPaginationStats.pages > 1 && (
                                <div className="join">
                                    <button 
                                        className="join-item btn btn-sm" 
                                        onClick={() => handleProductPageChange(1)}
                                        disabled={productPagination.page === 1}
                                    >
                                        «
                                    </button>
                                    <button 
                                        className="join-item btn btn-sm" 
                                        onClick={() => handleProductPageChange(productPagination.page - 1)}
                                        disabled={productPagination.page === 1}
                                    >
                                        ‹
                                    </button>
                                    
                                    {/* Renderizar números de página */}
                                    {Array.from({ length: Math.min(5, productPaginationStats.pages) }, (_, i) => {
                                        let pageNum;
                                        if (productPaginationStats.pages <= 5) {
                                            pageNum = i + 1;
                                        } else if (productPagination.page <= 3) {
                                            pageNum = i + 1;
                                        } else if (productPagination.page >= productPaginationStats.pages - 2) {
                                            pageNum = productPaginationStats.pages - 4 + i;
                                        } else {
                                            pageNum = productPagination.page - 2 + i;
                                        }
                                        
                                        return (
                                            <button
                                                key={pageNum}
                                                className={`join-item btn btn-sm ${productPagination.page === pageNum ? 'btn-active' : ''}`}
                                                onClick={() => handleProductPageChange(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                    
                                    <button 
                                        className="join-item btn btn-sm" 
                                        onClick={() => handleProductPageChange(productPagination.page + 1)}
                                        disabled={productPagination.page === productPaginationStats.pages}
                                    >
                                        ›
                                    </button>
                                    <button 
                                        className="join-item btn btn-sm" 
                                        onClick={() => handleProductPageChange(productPaginationStats.pages)}
                                        disabled={productPagination.page === productPaginationStats.pages}
                                    >
                                        »
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de edición */}
            {showEditModal && (
                <div className="modal modal-open">
                    <div className="modal-box w-11/12 max-w-2xl">
                        <h3 className="font-bold text-lg">Editar Artículo: {article.identificador}</h3>
                        <form onSubmit={handleSubmit} className="py-4">
                            <div className="grid grid-cols-1 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Nombre</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        name="nombre" 
                                        value={formData.nombre} 
                                        onChange={handleInputChange}
                                        className="input input-bordered w-full" 
                                        placeholder="Nombre del artículo"
                                        required
                                    />
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">País</span>
                                        </label>
                                        <select
                                            name="pais"
                                            value={formData.pais}
                                            onChange={handleInputChange}
                                            className="select select-bordered w-full"
                                        >
                                            <option value="">Sin país</option>
                                            {countryOptions.map(({ code, name }) => (
                                                <option key={code} value={code}>
                                                    {name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Ciudad</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            name="ciudad" 
                                            value={formData.ciudad} 
                                            onChange={handleInputChange}
                                            className="input input-bordered w-full" 
                                            placeholder="Ciudad del artículo"
                                        />
                                    </div>
                                </div>

                                <div className="form-control">
                                    <label className="cursor-pointer label">
                                        <span className="label-text">Artículo activo</span>
                                        <input 
                                            type="checkbox" 
                                            name="activo" 
                                            checked={formData.activo}
                                            onChange={handleInputChange}
                                            className="checkbox checkbox-primary" 
                                        />
                                    </label>
                                </div>
                            </div>
                            
                            <div className="modal-action">
                                <button type="button" className="btn" onClick={handleCloseModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArticleDetailScreen; 
